import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useActiveCompanyId } from '@/lib/active-company';
import { useAgencyStandardReport, type StandardReportEntry } from '@/lib/assessments';
import { cn } from '@/lib/cn';

type StandardStatus = 'meeting' | 'not_meeting' | 'not_applicable' | 'unanswered';

function statusOf(s: StandardReportEntry): StandardStatus {
  if (s.notApplicable === true) return 'not_applicable';
  if (s.meeting === true) return 'meeting';
  if (s.meeting === false) return 'not_meeting';
  return 'unanswered';
}

export function AgencyDrilldown() {
  const { code } = useParams<{ code: string }>();
  const companyId = useActiveCompanyId();
  const report = useAgencyStandardReport(companyId, code);

  if (!companyId) return <p className="p-10">Select a company first.</p>;
  if (!code) return <p className="p-10">Missing agency code.</p>;
  if (report.isLoading)
    return <p className="p-10 text-text-secondary">Loading…</p>;
  if (!report.data)
    return <p className="p-10 text-text-secondary">No data for this agency.</p>;

  const agency = report.data.summary.agencies.find((a) => a.code === code);
  if (!agency) {
    return (
      <Empty
        title="Agency not found"
        description="This agency has no standards in scope for the active company."
      />
    );
  }

  const standards = report.data.standards;
  const buckets = {
    not_meeting: standards.filter((s) => statusOf(s) === 'not_meeting'),
    meeting: standards.filter((s) => statusOf(s) === 'meeting'),
    not_applicable: standards.filter((s) => statusOf(s) === 'not_applicable'),
    unanswered: standards.filter((s) => statusOf(s) === 'unanswered'),
  };

  const pct = agency.total > 0 ? Math.round((agency.meeting / agency.total) * 100) : 0;

  return (
    <div className="mx-auto flex max-w-content-wide flex-col gap-gap-group px-5 py-gap-group md:px-10 lg:px-20">
      <header className="flex flex-wrap items-end justify-between gap-6 border-b border-border pb-gap-element-lg">
        <div>
          <p className="text-label uppercase tracking-[0.08em] text-accent">
            Agency drill-down
          </p>
          <h1 className="mt-2 font-display text-h1 font-bold tracking-tight">
            {agency.name}
          </h1>
          <p className="mt-2 text-body text-text-secondary">
            {agency.code} · {agency.total} standards · {pct}% meeting requirements
          </p>
        </div>
        <Link
          to="/report"
          className="text-body-sm font-medium text-text-secondary hover:text-accent"
        >
          ← Back to report
        </Link>
      </header>

      <section className="grid gap-gap-element-lg md:grid-cols-3">
        <Tile
          label="Meeting"
          value={String(agency.meeting)}
          subtext={`of ${agency.total}`}
          tone="success"
        />
        <Tile
          label="Not meeting"
          value={String(agency.notMeeting)}
          subtext={`of ${agency.total}`}
          tone={agency.notMeeting > 0 ? 'danger' : 'success'}
        />
        <Tile
          label="Sub-agencies"
          value={String(agency.subAgencies.length)}
          subtext="distinct"
          tone="neutral"
        />
      </section>

      {agency.subAgencies.length > 1 ? (
        <section
          data-testid="sub-agency-strip"
          className="rounded-lg border border-border bg-surface p-7"
        >
          <h2 className="font-display text-h3 font-semibold">By sub-agency</h2>
          <ul className="mt-5 grid gap-3 md:grid-cols-2">
            {agency.subAgencies.map((sa) => {
              const sp = sa.total > 0 ? Math.round((sa.meeting / sa.total) * 100) : 0;
              return (
                <li
                  key={sa.code}
                  className="flex items-center justify-between gap-4 rounded-md border border-border bg-background px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">
                      {sa.name}{' '}
                      <span className="text-text-secondary">({sa.code})</span>
                    </p>
                    <p className="text-body-sm text-text-secondary">
                      {sa.meeting}/{sa.total} meeting · {sa.notMeeting} not meeting
                    </p>
                  </div>
                  <span className="font-display text-h3 font-bold text-accent">
                    {sp}%
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <StandardsSection
        title="Not meeting"
        emptyHint="No standards flagged."
        standards={buckets.not_meeting}
        accent="danger"
      />
      <StandardsSection
        title="Unanswered"
        emptyHint="Every standard for this agency has a response."
        standards={buckets.unanswered}
        accent="warning"
      />
      <StandardsSection
        title="Meeting"
        emptyHint="No standards yet marked as meeting."
        standards={buckets.meeting}
        accent="success"
      />
      {buckets.not_applicable.length > 0 ? (
        <StandardsSection
          title="Not applicable"
          emptyHint=""
          standards={buckets.not_applicable}
          accent="muted"
        />
      ) : null}
    </div>
  );
}

function Tile({
  label,
  value,
  subtext,
  tone,
}: {
  label: string;
  value: string;
  subtext?: string;
  tone: 'success' | 'danger' | 'neutral';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'danger'
        ? 'text-danger'
        : 'text-text-primary';
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-7">
      <p className="text-label uppercase tracking-[0.08em] text-text-secondary">
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <span className={cn('font-display text-h3 font-bold', toneClass)}>
          {value}
        </span>
        {subtext ? (
          <span className="text-body-sm text-text-secondary">{subtext}</span>
        ) : null}
      </div>
    </div>
  );
}

function StandardsSection({
  title,
  emptyHint,
  standards,
  accent,
}: {
  title: string;
  emptyHint: string;
  standards: StandardReportEntry[];
  accent: 'success' | 'danger' | 'warning' | 'muted';
}) {
  const accentClass = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    muted: 'text-text-secondary',
  }[accent];
  return (
    <section
      data-testid={`agency-standards-${accent}`}
      className="rounded-lg border border-border bg-surface"
    >
      <div className="flex items-baseline justify-between border-b border-border p-7">
        <h2 className="font-display text-h3 font-semibold">{title}</h2>
        <span className={cn('text-body-sm font-medium', accentClass)}>
          {standards.length}
        </span>
      </div>
      {standards.length === 0 ? (
        <p className="p-7 text-body-sm text-text-secondary">{emptyHint}</p>
      ) : (
        <ul className="divide-y divide-border">
          {standards.map((s) => (
            <li key={`${s.subAgencyCode}-${s.code}`} className="px-7 py-4">
              <p className="font-medium text-text-primary">{s.code}</p>
              <p className="mt-1 text-body-sm text-text-secondary">
                {s.subAgencyName} ({s.subAgencyCode})
              </p>
              <p className="mt-2 text-body-sm text-text-primary">{s.source}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Empty({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mx-auto max-w-content-wide px-5 py-gap-group md:px-10 lg:px-20">
      <h1 className="font-display text-h1 font-bold">{title}</h1>
      {description ? (
        <p className="mt-3 text-body text-text-secondary">{description}</p>
      ) : null}
      <Button asChild variant="dark" className="mt-6">
        <Link to="/report">Back to report</Link>
      </Button>
    </div>
  );
}
